import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Cloud, Heart, Laptop, Loader } from "lucide-react";
import React, { useEffect, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { MediaGrid } from "../components/MediaGrid";
import { ProviderTabs } from "../components/ProviderTabs";
import { TrackList } from "../components/TrackList";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { useLocalLibraryStore } from "../stores/localLibraryStore";
import { usePlayerStore } from "../stores/playerStore";
import { usePlaylistStore } from "../stores/playlistStore";
import { useStatsStore } from "../stores/statsStore";
import { Track } from "../types";

type ContentType = "all" | "tracks" | "albums" | "playlists";

export const LikedSongs = () => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [contentType, setContentType] = useState<ContentType>("all");
  const { isAuthenticated, login } = useSpotifyAuth();
  const { getAllAlbums, getLikedContent, initialize: initializeLocalLibrary } = useLocalLibraryStore();
  const { files, initialize: initializeLocalFiles } = useLocalFilesStore();
  const { playlists, initialize: initializePlaylists } = usePlaylistStore();
  const { setCurrentTrack, setIsPlaying } = usePlayerStore();
  const { addPlayEvent } = useStatsStore();

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([initializeLocalLibrary(), initializeLocalFiles(), initializePlaylists()]);
    };

    initializeData();
  }, [initializeLocalLibrary, initializeLocalFiles, initializePlaylists]);

  // Get all liked local tracks
  const localLikedTracks = React.useMemo(() => {
    const likedContent = getLikedContent("track");
    const localLikedIds = likedContent.filter((item) => item.source === "local").map((item) => item.id);

    const tracks: Track[] = [];
    const addedTrackIds = new Set<string>();

    // Get tracks from local files
    files.forEach((file) => {
      if (localLikedIds.includes(file.id) && !addedTrackIds.has(file.id)) {
        tracks.push(file);
        addedTrackIds.add(file.id);
      }
    });

    // Get tracks from albums
    getAllAlbums().forEach((album) => {
      album.tracks.forEach((track) => {
        if (localLikedIds.includes(track.id) && !addedTrackIds.has(track.id)) {
          tracks.push({
            ...track,
            source: "local",
            album: album.name,
          });
          addedTrackIds.add(track.id);
        }
      });
    });

    // Get tracks from playlists
    playlists.forEach((playlist) => {
      playlist.tracks.forEach((track) => {
        if (localLikedIds.includes(track.id) && !addedTrackIds.has(track.id)) {
          tracks.push(track);
          addedTrackIds.add(track.id);
        }
      });
    });

    return tracks;
  }, [getLikedContent, getAllAlbums, files, playlists]);

  // Get liked local albums and playlists
  const localLikedAlbums = React.useMemo(() => {
    const likedAlbums = getLikedContent("album")
      .filter((item) => item.source === "local")
      .map((item) => getAllAlbums().find((album) => album.id === item.id))
      .filter(Boolean)
      .map((album) => ({
        id: album!.id,
        name: album!.name,
        artist: album!.tracks[0]?.artist || "Various Artists",
        coverUrl: album!.coverUrl || album!.tracks[0]?.albumArt,
        trackCount: album!.tracks.length || 0,
        source: "local" as const,
        type: "album" as const,
      }));
    return likedAlbums;
  }, [getLikedContent, getAllAlbums]);

  const localLikedPlaylists = React.useMemo(() => {
    const likedPlaylists = getLikedContent("playlist")
      .filter((item) => item.source === "local")
      .map((item) => playlists.find((playlist) => playlist.id === item.id))
      .filter(Boolean)
      .map((playlist) => ({
        id: playlist!.id,
        name: playlist!.name,
        artist: "Your Playlist",
        coverUrl: playlist!.icon,
        trackCount: playlist!.tracks.length || 0,
        source: "local" as const,
        type: "playlist" as const,
      }));
    return likedPlaylists;
  }, [getLikedContent, playlists]);

  // Get liked Spotify content
  const { data: spotifyLikedSongs, isLoading: isLoadingTracks } = useQuery({
    queryKey: ["liked-songs"],
    queryFn: () => spotifyApi.currentUser.tracks.savedTracks(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const { data: spotifyLikedAlbums, isLoading: isLoadingAlbums } = useQuery({
    queryKey: ["saved-albums"],
    queryFn: () => spotifyApi.currentUser.albums.savedAlbums(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const { data: spotifyLikedPlaylists, isLoading: isLoadingPlaylists } = useQuery({
    queryKey: ["followed-playlists"],
    queryFn: () => spotifyApi.currentUser.playlists.playlists(),
    enabled: isAuthenticated && activeProvider === "spotify",
  });

  const isLoading = isLoadingTracks || isLoadingAlbums || isLoadingPlaylists;

  const handlePlay = (track: Track) => {
    setCurrentTrack(track);
    setIsPlaying(true);
    addPlayEvent(track, 0, false);
  };

  const renderContent = () => {
    if (activeProvider === "spotify") {
      if (!isAuthenticated) {
        return (
          <EmptyState
            icon={Heart}
            title="Connect to Spotify"
            description="Connect your Spotify account to view your liked content"
            action={{
              label: "Connect Spotify",
              onClick: login,
            }}
          />
        );
      }

      if (isLoading) {
        return (
          <div className="flex flex-col items-center justify-center min-h-[50vh]">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
              <Loader className="w-8 h-8 animate-spin text-yellow-500" />
              <p className="text-zinc-400">Loading liked content from Spotify...</p>
            </motion.div>
          </div>
        );
      }

      const spotifyContent = {
        tracks:
          spotifyLikedSongs?.items.map((item) => ({
            id: item.track.id,
            title: item.track.name,
            artist: item.track.artists[0].name,
            albumArt: item.track.album.images[0]?.url,
            duration: item.track.duration_ms / 1000,
            url: item.track.preview_url || "",
            source: "spotify" as const,
            spotifyData: item.track,
          })) || [],
        albums:
          spotifyLikedAlbums?.items.map((item) => ({
            id: item.album.id,
            name: item.album.name,
            artist: item.album.artists[0].name,
            coverUrl: item.album.images[0]?.url,
            trackCount: item.album.total_tracks,
            source: "spotify" as const,
            type: "album" as const,
          })) || [],
        playlists:
          spotifyLikedPlaylists?.items.map((playlist) => ({
            id: playlist.id,
            name: playlist.name,
            artist: playlist.owner.display_name || "Unknown",
            coverUrl: playlist.images[0]?.url,
            trackCount: playlist.tracks?.total || 0,
            source: "spotify" as const,
            type: "playlist" as const,
          })) || [],
      };

      switch (contentType) {
        case "tracks":
          return spotifyContent.tracks.length ? (
            <TrackList
              tracks={spotifyContent.tracks}
              onPlay={handlePlay}
              title="Liked Songs"
              subtitle={`${spotifyContent.tracks.length} liked songs on Spotify`}
              image="https://misc.scdn.co/liked-songs/liked-songs-640.png"
            />
          ) : (
            <EmptyState
              icon={Heart}
              title="No liked songs"
              description="Like some songs on Spotify to see them here"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        case "albums":
          return spotifyContent.albums.length ? (
            <MediaGrid items={spotifyContent.albums} />
          ) : (
            <EmptyState
              icon={Heart}
              title="No liked albums"
              description="Like some albums on Spotify to see them here"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        case "playlists":
          return spotifyContent.playlists.length ? (
            <MediaGrid items={spotifyContent.playlists} />
          ) : (
            <EmptyState
              icon={Heart}
              title="No liked playlists"
              description="Like some playlists on Spotify to see them here"
              action={{
                label: "Browse Music",
                href: "/search",
              }}
            />
          );

        default:
          return (
            <div className="space-y-8">
              {spotifyContent.tracks.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Liked Songs</h2>
                  <TrackList tracks={spotifyContent.tracks.slice(0, 5)} onPlay={handlePlay} />
                </section>
              )}

              {spotifyContent.albums.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Liked Albums</h2>
                  <MediaGrid items={spotifyContent.albums.slice(0, 6)} />
                </section>
              )}

              {spotifyContent.playlists.length > 0 && (
                <section>
                  <h2 className="text-2xl font-bold mb-4">Liked Playlists</h2>
                  <MediaGrid items={spotifyContent.playlists.slice(0, 6)} />
                </section>
              )}
            </div>
          );
      }
    }

    // Local content
    switch (contentType) {
      case "tracks":
        return localLikedTracks.length ? (
          <TrackList
            tracks={localLikedTracks}
            onPlay={handlePlay}
            title="Liked Songs"
            subtitle={`${localLikedTracks.length} liked local songs`}
            image="https://misc.scdn.co/liked-songs/liked-songs-640.png"
          />
        ) : (
          <EmptyState
            icon={Heart}
            title="No liked songs"
            description="Like some local songs to see them here"
            action={{
              label: "Browse Music",
              href: "/local",
            }}
          />
        );

      case "albums":
        return localLikedAlbums.length ? (
          <MediaGrid items={localLikedAlbums} />
        ) : (
          <EmptyState
            icon={Heart}
            title="No liked albums"
            description="Like some local albums to see them here"
            action={{
              label: "Browse Albums",
              href: "/albums",
            }}
          />
        );

      case "playlists":
        return localLikedPlaylists.length ? (
          <MediaGrid items={localLikedPlaylists} />
        ) : (
          <EmptyState
            icon={Heart}
            title="No liked playlists"
            description="Like some local playlists to see them here"
            action={{
              label: "Browse Playlists",
              href: "/playlists",
            }}
          />
        );

      default:
        return (
          <div className="space-y-8">
            {localLikedTracks.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Liked Songs</h2>
                <TrackList tracks={localLikedTracks.slice(0, 5)} onPlay={handlePlay} />
              </section>
            )}

            {localLikedAlbums.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Liked Albums</h2>
                <MediaGrid items={localLikedAlbums.slice(0, 6)} />
              </section>
            )}

            {localLikedPlaylists.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-4">Liked Playlists</h2>
                <MediaGrid items={localLikedPlaylists.slice(0, 6)} />
              </section>
            )}

            {localLikedTracks.length === 0 && localLikedAlbums.length === 0 && localLikedPlaylists.length === 0 && (
              <EmptyState
                icon={Heart}
                title="No liked content"
                description="Like some songs, albums or playlists to see them here"
                action={{
                  label: "Browse Music",
                  href: "/local",
                }}
              />
            )}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-900/50 to-zinc-900">
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">Liked Content</h1>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setContentType("all")}
                  className={`text-sm ${contentType === "all" ? "text-white" : "text-zinc-400"}`}
                >
                  All
                </button>
                <button
                  onClick={() => setContentType("tracks")}
                  className={`text-sm ${contentType === "tracks" ? "text-white" : "text-zinc-400"}`}
                >
                  Songs
                </button>
                <button
                  onClick={() => setContentType("albums")}
                  className={`text-sm ${contentType === "albums" ? "text-white" : "text-zinc-400"}`}
                >
                  Albums
                </button>
                <button
                  onClick={() => setContentType("playlists")}
                  className={`text-sm ${contentType === "playlists" ? "text-white" : "text-zinc-400"}`}
                >
                  Playlists
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-zinc-400 mt-2 mb-8">
          <div className="flex items-center gap-1">
            <div className="p-1 bg-[#1DB954] rounded-full">
              <Cloud className="w-3 h-3" />
            </div>
            <span>Spotify</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="p-1 bg-zinc-700 rounded-full">
              <Laptop className="w-3 h-3" />
            </div>
            <span>Local</span>
          </div>
        </div>

        <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />

        {renderContent()}
      </div>
    </div>
  );
};
